// ============================================================
// ClientDetailPage.js
// Route /clients/:clientId — single-Client management surface.
//
// Layout:
//   <PageHeader> (Client name + optional display name)
//   <ClientDetailMeta> (type badge, lifecycle badge, tenant link)
//   <Tabs> (Users | Group Mappings | Modules | Usage | History)
//   <ClientDetailTabContent> — renders the active tab
//
// URL state:
//   ?tab=users|group-mappings|modules|usage|history  (default: users)
//
// Header endpoint is fetched once on mount / clientId change. The
// active tab is responsible for its own fetching.
//
// 404 handling: the header endpoint's not-found surfaces inline
// (not via the global toast) — operators routinely arrive here
// from a stale link, and a noisy error toast is the wrong UX.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import Tabs from '../components/Tabs';
import Badge from '../components/Badge';
import ClientLifecyclePanel from '../components/ClientLifecyclePanel';
import Breadcrumb from '../components/Breadcrumb';
import { LifecycleStatusBadge } from '../components/StatusBadge';
import { getAdminClientDetail } from '../api/adminClients';
import { translateError } from '../utils/errorMessages';
import ClientUsersTab from './ClientUsersTab';
import RenameClientDialog from './RenameClientDialog';
import GroupMappingsTab from './GroupMappingsTab';
import TenantSearchTab from './TenantSearchTab';
import ClientModulesTab from './ClientModulesTab';
import ClientUsageTab from './ClientUsageTab';
import ClientHistoryTab from './ClientHistoryTab';
import ClientPlatformConfigTab from './ClientPlatformConfigTab';
import ClientBillingTab from './ClientBillingTab';
import ClientDeletedDataTab from './ClientDeletedDataTab';
import ClientExportsTab from './ClientExportsTab';
import ManualInviteBanner from './ManualInviteBanner';
import StatusMessage from '../components/StatusMessage';
import './ClientDetailPage.css';

const TAB_IDS = ['users', 'group-mappings', 'tenant-search', 'modules', 'platform-config', 'billing', 'usage', 'deleted-data', 'exports', 'history'];
const DEFAULT_TAB = 'users';

const CLIENT_TYPE_LABEL = {
  care_home:          'Care home',
  domiciliary_agency: 'Domiciliary agency',
  mixed:              'Mixed',
};

const TAB_DEFS = [
  { id: 'users',           label: 'Users' },
  { id: 'group-mappings',  label: 'Group Mappings' },
  { id: 'tenant-search',   label: 'Tenant search' },
  { id: 'modules',         label: 'Modules' },
  { id: 'platform-config', label: 'Platform Config' },
  { id: 'billing',         label: 'Billing' },
  { id: 'usage',           label: 'Usage' },
  { id: 'deleted-data',    label: 'Deleted Data' },
  { id: 'exports',         label: 'Exports' },
  { id: 'history',         label: 'History' },
];

export default function ClientDetailPage() {
  const { clientId: clientIdParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const clientId = parseInt(clientIdParam, 10);
  const isValidId = Number.isInteger(clientId) && clientId > 0;

  const rawTab = searchParams.get('tab');
  const activeTab = TAB_IDS.includes(rawTab) ? rawTab : DEFAULT_TAB;

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);

  const fetchHeader = useCallback(async () => {
    if (!isValidId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const data = await getAdminClientDetail(clientId);
      setClient(data);
    } catch (err) {
      if (err && err.status === 404) {
        setNotFound(true);
      } else {
        setError(translateError(err));
      }
    } finally {
      setLoading(false);
    }
  }, [clientId, isValidId]);

  useEffect(() => { fetchHeader(); }, [fetchHeader]);

  function handleTabChange(nextTab) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nextTab === DEFAULT_TAB) next.delete('tab'); else next.set('tab', nextTab);
      // Tab-specific filter/page state is reset on tab change — it
      // belongs to the leaving tab, not the arriving one.
      ['page', 'pageSize', 'startDate', 'endDate', 'groupBy', 'action', 'entityType', 'roleKey']
        .forEach((k) => next.delete(k));
      return next;
    });
  }

  if (loading) {
    return (
      <>
        <PageHeader title="Loading…" />
        <StatusMessage variant="loading" className="client-detail__state">Loading client…</StatusMessage>
      </>
    );
  }

  if (notFound) {
    return (
      <>
        <PageHeader title="Client not found" />
        <StatusMessage variant="empty" className="client-detail__state client-detail__state--empty">
          <p>That Client does not exist or has been removed.</p>
          <Link to="/clients" className="client-detail__back-link">
            ← Return to Clients list
          </Link>
        </StatusMessage>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader title="Client" />
        <StatusMessage variant="error" className="client-detail__state client-detail__state--error">
          <p>{error}</p>
          <button type="button" className="client-detail__retry" onClick={fetchHeader}>
            Retry
          </button>
        </StatusMessage>
      </>
    );
  }

  const typeLabel = CLIENT_TYPE_LABEL[client.clientType] || client.clientType;

  // Hierarchical breadcrumb above the page header. When the Client
  // has a parent tenant (typical for managed Pathway-1/2 clients),
  // the full trail is Tenants › {tenantName} › Clients (filtered
  // to this tenant) › {clientName}. For Pathway-3 (null tenant)
  // the middle two segments collapse to a single unfiltered Clients
  // link. The existing "Belongs to {tenantName}" line in the meta
  // strip is intentionally retained — the breadcrumb is for
  // navigation; the meta-field communicates the parent relationship
  // as a first-class property of the record.
  const breadcrumbItems = [
    { label: 'Tenants', to: '/tenants' },
    ...(client.tenantId != null && client.tenantName
      ? [
          { label: client.tenantName, to: `/tenants/${client.tenantId}` },
          { label: 'Clients',         to: `/clients?tenantId=${client.tenantId}` },
        ]
      : [
          { label: 'Clients', to: '/clients' },
        ]),
    { label: client.clientName },
  ];

  return (
    <>
      <Breadcrumb items={breadcrumbItems} />

      <PageHeader
        title={client.clientName}
        subtitle={client.displayName && client.displayName !== client.clientName
          ? client.displayName
          : undefined}
        cta={
          <button
            type="button"
            className="client-detail__rename"
            onClick={() => setIsRenameOpen(true)}
          >
            <Pencil size={16} aria-hidden="true" />
            <span>Rename client</span>
          </button>
        }
      />

      {isRenameOpen && (
        <RenameClientDialog
          client={client}
          isOpen={isRenameOpen}
          onClose={() => setIsRenameOpen(false)}
          onSaved={() => fetchHeader()}
        />
      )}

      {client.manualInviteRequired && (
        <ManualInviteBanner
          clientId={clientId}
          manualInviteReason={client.manualInviteReason}
          onCleared={fetchHeader}
        />
      )}

      <div className="client-detail__meta" data-testid="client-detail-meta">
        <Badge variant="neutral">{typeLabel}</Badge>
        <LifecycleStatusBadge status={client.lifecycleStatus} />
        <span className="client-detail__tenant">
          {client.tenantId != null && client.tenantName ? (
            <>
              Belongs to{' '}
              <Link to={`/tenants/${client.tenantId}`}>{client.tenantName}</Link>
            </>
          ) : (
            <span className="client-detail__tenant--none">No tenant (Pathway 3)</span>
          )}
        </span>
      </div>

      {/* Client-level lifecycle. The badge above SHOWS the state; this is
          the only place it can be MOVED (Platform-06 shipped the state
          machine with no portal binding). */}
      <ClientLifecyclePanel client={client} onChanged={fetchHeader} />

      <Tabs tabs={TAB_DEFS} active={activeTab} onChange={handleTabChange} />

      <div className="client-detail__tab-content">
        {activeTab === 'users' && (
          <ClientUsersTab clientId={clientId} />
        )}
        {activeTab === 'group-mappings' && (
          <GroupMappingsTab clientId={clientId} />
        )}
        {activeTab === 'tenant-search' && (
          <TenantSearchTab clientId={clientId} />
        )}
        {activeTab === 'modules' && (
          <ClientModulesTab clientId={clientId} clientName={client.clientName} />
        )}
        {activeTab === 'platform-config' && (
          <ClientPlatformConfigTab clientId={clientId} clientName={client.clientName} />
        )}
        {activeTab === 'billing' && (
          <ClientBillingTab clientId={clientId} />
        )}
        {activeTab === 'usage' && (
          <ClientUsageTab clientId={clientId} />
        )}
        {activeTab === 'deleted-data' && (
          <ClientDeletedDataTab clientId={clientId} />
        )}
        {activeTab === 'exports' && (
          <ClientExportsTab clientId={clientId} clientName={client.clientName} />
        )}
        {activeTab === 'history' && (
          <ClientHistoryTab clientId={clientId} />
        )}
      </div>
    </>
  );
}

export { TAB_IDS, DEFAULT_TAB, CLIENT_TYPE_LABEL };
